import {
  StackLayout,
  UtilityText,
  Divider,
  StatusBanner,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { StepScreen } from 'components/step-screen/step-screen.component';
import { useStepNavigation } from 'components/stepper/stepper.component';
import {
  useOnboardingFormActions,
  useOnboardingFormState,
} from 'components/onboarding-account-setup/contexts/onboarding-form.context';
import { STEP_STRING_IDS } from '../../onboarding-account-setup.constants';
import { PersonalInformationData } from '../../types/onboarding-account-setup-types';

interface PersonalReviewItemProps {
  label: string;
  value: string;
}

const PersonalReviewItem = ({ label, value }: PersonalReviewItemProps) => (
  <StackLayout spacing="$quarter">
    <UtilityText size="base" emphasis="emphasized">
      {label}
    </UtilityText>
    <UtilityText size="base" emphasis="base">
      {value}
    </UtilityText>
  </StackLayout>
);

const PersonalReviewBody = ({
  dateOfBirth,
  firstName,
  lastName,
}: PersonalInformationData) => {
  const { formatMessage } = useIntl();

  const formatDateOfBirth = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <StackLayout spacing="$one" css={{ width: '100%' }} divider={<Divider />}>
      <PersonalReviewItem
        label={formatMessage({
          id: 'STR_ONBOARDING_ACCOUNT_SETUP_PERSONAL_INFO_FIRST_NAME_LABEL',
        })}
        value={firstName}
      />
      <PersonalReviewItem
        label={formatMessage({
          id: 'STR_ONBOARDING_ACCOUNT_SETUP_PERSONAL_INFO_LAST_NAME_LABEL',
        })}
        value={lastName}
      />
      <PersonalReviewItem
        label={formatMessage({
          id: 'STR_ONBOARDING_ACCOUNT_SETUP_PERSONAL_INFO_DOB_LABEL',
        })}
        value={formatDateOfBirth(dateOfBirth)}
      />
    </StackLayout>
  );
};

export const PersonalInfoReviewStep = () => {
  const { formatMessage } = useIntl();
  const { goToNextStep, goToPreviousStep } = useStepNavigation();
  const {
    formState: { personalInformation },
    setUserProfileStatus,
  } = useOnboardingFormState();
  const { submitPersonalInformation } = useOnboardingFormActions();

  const handleSubmit = () => {
    submitPersonalInformation()
      .then(() => {
        goToNextStep();
      })
      .catch(() => {
        // Error surfaced via setUserProfileStatus.isError below
      });
  };

  return (
    <StepScreen
      headerContent={
        setUserProfileStatus.isError ? (
          <StatusBanner
            status="error"
            title={formatMessage({
              id: 'STR_ONBOARDING_ACCOUNT_SETUP_PERSONAL_INFO_REVIEW_ERROR_TITLE',
            })}
            css={{
              width: '100%',
              border: '$borderWidths$thin solid $criticalBorderDefault',
              svg: {
                color: '$warningIcon',
              },
            }}
          >
            {formatMessage({
              id: 'STR_ONBOARDING_ACCOUNT_SETUP_PERSONAL_INFO_REVIEW_ERROR_DESCRIPTION',
            })}
          </StatusBanner>
        ) : undefined
      }
      heading={formatMessage({
        id: STEP_STRING_IDS.personalInfoReview.heading,
      })}
      subHeading={formatMessage({
        id: STEP_STRING_IDS.personalInfoReview.subHeading,
      })}
      primaryButton={{
        label: formatMessage({
          id: STEP_STRING_IDS.personalInfoReview.primaryButtonLabel,
        }),
        onClick: handleSubmit,
        isButtonLoading: setUserProfileStatus.isPending,
      }}
      secondaryButton={{
        label: formatMessage({
          id: STEP_STRING_IDS.personalInfoReview.secondaryButtonLabel,
        }),
        onClick: goToPreviousStep,
      }}
    >
      <PersonalReviewBody {...personalInformation} />
    </StepScreen>
  );
};
